import { putAndHandleAPIError, selectAPI } from '@api/API.sagas';
import { isErrorResponse } from '@api/HelperAPI';
import { fetchCreateSetupIntent } from '@api/lendingAPI/LendingAPI';
import {
    API_GET_PAYMENT_METHOD,
    API_GET_PAYMENT_METHOD_SUCCESS,
    API_POST_PAYMENT_METHOD
} from '@api/lendingAPI/LendingAPI.actions';
import { LendingAPIError } from '@api/lendingAPI/LendingAPIError';
import {
    isValidCardType,
    serialiseBankAccountData
} from '@api/lendingAPI/PaymentAPIHelper';
import { getCountry } from '@iwIntlProvider/CountryProvider';
import { logError } from '@iwLogger/IwDataDogLogger';
import { OPEN_MODAL } from '@iwModals/Modals.actions';
import { delay, eventChannel } from 'redux-saga';
import { call, fork, put, select, take, takeLatest } from 'redux-saga/effects';

import * as actions from './AddPaymentMethodModal.actions';
import { detectBankAccountLinked } from '../../Profile/ProfileHelper';

export const addPaymentMethodModalSagaWorkers = [
    fork(addPaymentMethod),
    fork(choosePaymentMethodDebitCardWorker),
    fork(registerBankAccount),
    fork(registerDEBankAccount),
    fork(registerDebitCard),
    fork(handleFieldChange),
    fork(refetchPaymentMethod),
    fork(checkoutDebitCardRegistered)
];

function* addPaymentMethod() {
    yield takeLatest(actions.ADD_PAYMENT_METHOD, function* (action) {
        switch (action.paymentMethodType) {
            case 'DEBIT_CARD':
                yield initialisePaymentMethodDebitCard();
                yield put({
                    type: OPEN_MODAL,
                    id: 'AddPaymentMethodModal',
                    extra: action.paymentMethodType
                });
                break;
            case 'BANK_ACCOUNT':
                yield put({ type: actions.CHOOSE_PAYMENT_METHOD_BANK_ACCOUNT });
                yield put({
                    type: OPEN_MODAL,
                    id: 'AddPaymentMethodModal',
                    extra: action.paymentMethodType
                });
                if (getCountry() === 'DE') {
                    yield putAndHandleAPIError({
                        type: API_GET_PAYMENT_METHOD
                    });
                }
                break;
            default:
                yield put({ type: actions.CHOOSE_PAYMENT_METHOD });
                yield put({
                    type: OPEN_MODAL,
                    id: 'AddPaymentMethodModal',
                    extra: action.paymentMethodType
                });
                break;
        }
    });
}

function* choosePaymentMethodDebitCardWorker() {
    yield takeLatest(
        actions.INITIALISE_PAYMENT_METHOD_DEBIT_CARD,
        initialisePaymentMethodDebitCard
    );
}

function* initialisePaymentMethodDebitCard() {
    const { payment_method } = yield selectAPI();

    yield put({
        type: actions.CHOOSE_PAYMENT_METHOD_DEBIT_CARD,
        payment_method
    });

    yield put({ type: actions.TOGGLE_ADD_DEBIT_CARD_FORM });
}

function* registerBankAccount() {
    yield takeLatest(actions.REGISTER_BANK_ACCOUNT, function* (action) {
        yield put({ type: actions.REGISTER_BANK_ACCOUNT_PENDING });

        const data = serialiseBankAccountData(action);
        const serialiseBody = JSON.stringify({ data });

        yield putAndHandleAPIError({
            type: API_POST_PAYMENT_METHOD,
            body: serialiseBody
        });
        yield putAndHandleAPIError({ type: API_GET_PAYMENT_METHOD });

        const { payment_method } = yield selectAPI();
        yield put({
            type: actions.ADD_PAYMENT_METHOD_COMPLETED,
            payment_method
        });

        yield put({ type: actions.REGISTER_BANK_ACCOUNT_SUCCESS });
    });
}

function* registerDEBankAccount() {
    yield takeLatest(actions.REGISTER_DE_BANK_ACCOUNT, function* (action) {
        yield put({ type: actions.REGISTER_BANK_ACCOUNT_PENDING });

        const { payment_method } = yield selectAPI();
        if (!detectBankAccountLinked(payment_method)) {
            const data = serialiseBankAccountData(action);
            const serialiseBody = JSON.stringify({ data: data });

            yield putAndHandleAPIError({
                type: API_POST_PAYMENT_METHOD,
                body: serialiseBody
            });
            yield putAndHandleAPIError({ type: API_GET_PAYMENT_METHOD });
            const { payment_method } = yield selectAPI();
            yield put({
                type: actions.ADD_PAYMENT_METHOD_COMPLETED,
                payment_method
            });
        }

        try {
            const response = yield call(fetchCreateSetupIntent, {
                body: JSON.stringify({
                    data: {
                        payment_method_type: 'sepa_debit',
                        role: 'primary'
                    }
                })
            });
            if (isErrorResponse(response[1])) {
                throw new LendingAPIError(response);
            }
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (error) {
            yield put({ type: actions.REGISTER_BANK_ACCOUNT_FAIL });
            return;
        }

        yield put({ type: actions.REGISTER_BANK_ACCOUNT_SUCCESS });
    });
}

function* fetchPaymentMethodCount() {
    yield put({ type: API_GET_PAYMENT_METHOD });
    const {
        response: { data }
    } = yield take(API_GET_PAYMENT_METHOD_SUCCESS);
    return data.length;
}

function* registerDebitCard() {
    yield takeLatest(
        actions.REGISTER_DEBIT_CARD,
        function* ({ stripe, billingPostcode, cardholderName, repaymentRole }) {
            yield put({ type: actions.REGISTER_DEBIT_CARD_PENDING });

            const paymentMethodCountBefore = yield call(
                fetchPaymentMethodCount
            );

            const body = JSON.stringify({
                data: {
                    payment_method_type: 'card',
                    role: repaymentRole
                }
            });

            try {
                const [{ data, errors }] = yield call(fetchCreateSetupIntent, {
                    body
                });

                if (errors) {
                    logError('[Stripe] Setup intent creation failed', {
                        errors
                    });
                    return yield put({
                        type: actions.AUTHENTICATION_FAILED
                    });
                }

                const cardNumberElement = yield select(
                    ({ addPaymentMethodModal }) =>
                        addPaymentMethodModal.cardNumberElement
                );
                let {
                    error: paymentMethodError,
                    paymentMethod: paymentMethodSuccess
                } = yield call(() =>
                    stripe.createPaymentMethod({
                        type: 'card',
                        card: cardNumberElement,
                        billing_details: {
                            name: cardholderName,
                            address: { postal_code: billingPostcode }
                        }
                    })
                );

                if (paymentMethodError) {
                    logError('[Stripe] Payment method creation failed', {
                        code: paymentMethodError.code,
                        message: paymentMethodError.message
                    });
                    return yield put({ type: actions.AUTHENTICATION_FAILED });
                }
                if (
                    !isValidCardType(
                        paymentMethodSuccess,
                        data.accepted_card_types
                    )
                ) {
                    logError(
                        '[Stripe] Payment method is not an accepted type',
                        {
                            cardType: paymentMethodSuccess.card.funding,
                            acceptedTypes: data.accepted_card_types
                        }
                    );
                    return yield put({
                        type: actions.CARD_TYPE_NOT_DEBIT
                    });
                }

                let { error, setupIntent } = yield call(() =>
                    stripe.confirmSetupIntent(data.secret, cardNumberElement, {
                        payment_method_data: {
                            billing_details: {
                                name: cardholderName,
                                address: { postal_code: billingPostcode }
                            }
                        },
                        return_url:
                            'https://pandora.iwoca.com/prod/uk/stripe/authentication_finished.html'
                    })
                );

                if (error) {
                    logError('[Stripe] Setup intent confirmation failed', {
                        code: error.code,
                        message: error.message,
                        declineCode: error.decline_code
                    });
                    return yield put({ type: actions.AUTHENTICATION_FAILED });
                }

                if (setupIntent.status === 'requires_action') {
                    yield put({
                        type: actions.HANDLE_3DS_AUTHENTICATION,
                        authenticationIframeURL:
                            setupIntent.next_action.redirect_to_url.url
                    });

                    yield fork(authenticationCompleteWorker);

                    yield take(actions.AUTHENTICATION_FINISHED);
                    let { setupIntent: confirmedSetupIntent } = yield call(() =>
                        stripe.retrieveSetupIntent(data.secret)
                    );

                    if (confirmedSetupIntent.last_setup_error) {
                        logError(
                            '[Stripe] Setup intent confirmation failed after 3DS',
                            {
                                code: confirmedSetupIntent.last_setup_error
                                    .code,
                                message:
                                    confirmedSetupIntent.last_setup_error
                                        .message
                            }
                        );
                        yield put({ type: actions.AUTHENTICATION_FAILED });
                    } else {
                        yield refreshPaymentMethodsAndClose(
                            paymentMethodCountBefore
                        );
                    }
                }

                if (setupIntent.status === 'succeeded') {
                    yield refreshPaymentMethodsAndClose(
                        paymentMethodCountBefore
                    );
                }
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
            } catch (error) {
                yield put({ type: actions.AUTHENTICATION_FAILED });
            }
        }
    );
}

function* handleFieldChange() {
    yield takeLatest(actions.HANDLE_STRIPE_FIELD_CHANGE, function* (action) {
        const {
            field,
            response: { error, complete, brand }
        } = action;

        if (complete) {
            yield put({ type: actions.CLEAR_STRIPE_ERROR, field: field });
            yield put({ type: actions.STRIPE_FIELD_COMPLETE, field: field });
        } else {
            yield put({ type: actions.CLEAR_STRIPE_ERROR, field: field });
        }
        if (error) {
            yield put({
                type: actions.SHOW_STRIPE_ERROR,
                field: field,
                errors: error
            });
        } else if (brand) {
            yield put({ type: actions.SHOW_STRIPE_BRAND, brand: brand });
        }
    });
}

export function* pollPaymentMethodsUntilCountGreaterThan(previousCount) {
    let remainingAttempts = 10;
    let currentCount = 0;
    while (currentCount <= previousCount && remainingAttempts) {
        yield call(delay, 3000);
        yield put({ type: API_GET_PAYMENT_METHOD });
        const {
            response: { data }
        } = yield take(API_GET_PAYMENT_METHOD_SUCCESS);
        currentCount = data.length;
        if (currentCount > previousCount) return data;
        else remainingAttempts--;
    }
    // Something went wrong while getting the new payment method.
    // We won't be showing it at the moment but the app can continue working normally.
    return null;
}

function* refreshPaymentMethodsAndClose(paymentMethodCountBefore) {
    // Called when setupIntent succeeds

    const payment_method = yield call(
        pollPaymentMethodsUntilCountGreaterThan,
        paymentMethodCountBefore
    );

    yield put({
        type: actions.ADD_PAYMENT_METHOD_COMPLETED,
        payment_method
    });
    yield put({ type: actions.REGISTER_DEBIT_CARD_SUCCESS });
}

export function* authenticationCompleteWorker() {
    const channel = yield call(authenticationCompleteChannel);

    while (true) {
        const authenticationComplete = yield take(channel);
        if (authenticationComplete) {
            yield put({
                type: actions.AUTHENTICATION_FINISHED,
                authenticationComplete
            });
            channel.close();
        }
    }
}

function authenticationCompleteChannel() {
    return eventChannel(function subscriber(emitter) {
        const checkAuthenticationComplete = ({ data }) => {
            if (data === '3DS-authentication-complete') {
                emitter(true);
            } else {
                emitter(false);
            }
        };

        const listener = () =>
            window.addEventListener('message', checkAuthenticationComplete);
        listener();

        return function unsubscribe() {
            window.removeEventListener('message', listener);
        };
    });
}

function* checkoutDebitCardRegistered() {
    yield takeLatest(
        actions.CHECKOUT_DEBIT_CARD_REGISTERED,
        function* ({ paymentMethodCountBefore }) {
            yield* refreshPaymentMethodsAndClose(paymentMethodCountBefore);
        }
    );
}

function* refetchPaymentMethod() {
    yield takeLatest(actions.REFETCH_PAYMENT_METHOD, function* () {
        yield putAndHandleAPIError({ type: API_GET_PAYMENT_METHOD });
        const { payment_method } = yield selectAPI();
        yield put({
            type: actions.ADD_PAYMENT_METHOD_COMPLETED,
            payment_method
        });
    });
}
